import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Wifi, Activity, Signal, Zap, Thermometer,
  Router, RefreshCw, Power, Settings, Download, Upload,
  Users, Clock, HardDrive, Cpu, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DeviceDetail = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [device, setDevice] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [wifiSsid, setWifiSsid] = useState('');
  const [wifiPassword, setWifiPassword] = useState('');
  const [showWifiForm, setShowWifiForm] = useState(false);

  useEffect(() => {
    fetchDeviceDetail();
  }, [deviceId]);

  const fetchDeviceDetail = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${BACKEND_URL}/api/devices/${deviceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setDevice(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching device:', error);
      toast.error('Failed to load device details');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDeviceDetail();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleDeviceAction = async (action) => {
    if (!window.confirm(`${action} this device?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/devices/${deviceId}/operations`,
        { operation: action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${action} command sent successfully`);
      setTimeout(() => fetchDeviceDetail(), 2000);
    } catch (error) {
      toast.error(`Failed to ${action} device`);
    }
  };

  const handleWifiUpdate = async () => {
    // Validate password length
    if (wifiPassword && wifiPassword.length < 8) {
      toast.error('Password WiFi minimal 8 karakter');
      return;
    }

    if (!wifiSsid && !wifiPassword) {
      toast.error('Masukkan SSID atau Password yang ingin diubah');
      return;
    }

    if (!window.confirm('Update WiFi settings?')) return;

    try {
      const token = localStorage.getItem('token');
      const payload = {};
      if (wifiSsid) payload.ssid = wifiSsid;
      if (wifiPassword) payload.password = wifiPassword;

      await axios.post(
        `${BACKEND_URL}/api/devices/${deviceId}/wifi/update`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('WiFi settings updated successfully');
      setWifiSsid('');
      setWifiPassword('');
      setShowWifiForm(false);
      setTimeout(() => fetchDeviceDetail(), 2000);
    } catch (error) {
      toast.error('Failed to update WiFi settings');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-600 dark:text-slate-300">Loading device details...</p>
        </div>
      </div>
    );
  }

  if (!device) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <h2 className="text-xl font-semibold text-gray-900 mb-2 dark:text-slate-100">Device Not Found</h2>
          <button
            onClick={() => navigate('/devices')}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Back to Devices
          </button>
        </div>
      </div>
    );
  }

  const stats = device.stats || {};
  const optical = device.optical || {};
  const network = device.network || {};
  const system = device.system || {};

  // Function to get optical power color (red/yellow/green based on value)
  const getOpticalPowerColor = (power) => {
    if (!power) return 'text-gray-600';
    const value = parseFloat(power.replace(/[^\d.-]/g, ''));
    
    // For RX Power (should be between -28 and -8 dBm)
    if (value < -27) return 'text-red-600'; // Too low
    if (value < -20) return 'text-yellow-600'; // Warning
    if (value < -8) return 'text-green-600'; // Good
    return 'text-gray-600';
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full">
      {/* Header */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/devices')}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-3 dark:text-slate-300"
        >
          <ArrowLeft size={16} />
          Back to Devices
        </button>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-50 rounded-lg dark:bg-indigo-900/30">
              <Router className="text-indigo-600 dark:text-indigo-400" size={24} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-slate-100">{device.model}</h1>
              <p className="text-xs text-gray-500 mt-0.5 dark:text-slate-400">SN: {device.serial_number}</p>
              <p className="text-xs text-gray-400 font-mono">{device.mac_address}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1.5 rounded-md text-xs font-medium border ${
              device.status === 'online'
                ? 'bg-green-50 text-green-700 border-green-200'
                : 'bg-gray-50 text-gray-700 border-gray-200'
            }`}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${
                device.status === 'online' ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
              }`}></span>
              {device.status === 'online' ? 'Online' : 'Offline'}
            </span>
            
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 text-xs dark:border-slate-600 dark:hover:bg-slate-700/50"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Optical Power Section (REDAMAN) - HIGHLIGHTED */}
      {optical && Object.keys(optical).length > 0 && (
        <div className="mb-4">
          <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-1.5 dark:text-slate-100">
            <Zap className="text-yellow-600 dark:text-yellow-400" size={16} />
            Optical Power (Redaman)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* RX Power */}
            <div className="bg-white rounded-lg border-2 border-yellow-200 shadow-sm p-4 dark:bg-slate-800">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-600 dark:text-slate-300">RX Power (ONT)</p>
                <Signal className={getOpticalPowerColor(optical.rx_power)} size={16} />
              </div>
              <p className={`text-2xl font-bold ${getOpticalPowerColor(optical.rx_power)}`}>
                {optical.rx_power || '-'}
              </p>
              <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                Normal: -28 ~ -8 dBm
              </p>
            </div>

            {/* TX Power */}
            <div className="bg-white rounded-lg border-2 border-blue-200 shadow-sm p-4 dark:bg-slate-800">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-600 dark:text-slate-300">TX Power (ONT)</p>
                <Zap className="text-blue-600 dark:text-blue-400" size={16} />
              </div>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {optical.tx_power || '-'}
              </p>
              <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                Normal: +2 ~ +7 dBm
              </p>
            </div>

            {/* OLT RX Power */}
            <div className="bg-white rounded-lg border-2 border-purple-200 shadow-sm p-4 dark:bg-slate-800">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-600 dark:text-slate-300">OLT RX Power</p>
                <Activity className="text-purple-600 dark:text-purple-400" size={16} />
              </div>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {optical.olt_rx_power || '-'}
              </p>
              <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                From OLT to ONT
              </p>
            </div>
          </div>

          {/* Additional Optical Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 dark:bg-slate-800 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <Thermometer className="text-orange-600 dark:text-orange-400" size={16} />
                <div>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Temperature</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">{optical.temperature || '-'}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 dark:bg-slate-800 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <Zap className="text-yellow-600 dark:text-yellow-400" size={16} />
                <div>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Voltage</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">{optical.voltage || '-'}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 dark:bg-slate-800 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <Activity className="text-indigo-600 dark:text-indigo-400" size={16} />
                <div>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Bias Current</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">{optical.bias_current || '-'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Network Stats */}
      {stats.bandwidth && (
        <div className="mb-4">
          <h2 className="text-base font-semibold text-gray-900 mb-3 dark:text-slate-100">Bandwidth Usage</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 dark:bg-slate-800 dark:border-slate-700">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs text-gray-600 dark:text-slate-300">Download Speed</p>
                <Download className="text-green-600 dark:text-green-400" size={16} />
              </div>
              <p className="text-xl font-bold text-green-600 dark:text-green-400">
                {stats.bandwidth.download_mbps} Mbps
              </p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 dark:bg-slate-800 dark:border-slate-700">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs text-gray-600 dark:text-slate-300">Upload Speed</p>
                <Upload className="text-blue-600 dark:text-blue-400" size={16} />
              </div>
              <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                {stats.bandwidth.upload_mbps} Mbps
              </p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 dark:bg-slate-800 dark:border-slate-700">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs text-gray-600 dark:text-slate-300">Total Download</p>
                <HardDrive className="text-gray-600 dark:text-slate-300" size={16} />
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-slate-100">
                {stats.bandwidth.total_download_gb} GB
              </p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 dark:bg-slate-800 dark:border-slate-700">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs text-gray-600 dark:text-slate-300">Total Upload</p>
                <HardDrive className="text-gray-600 dark:text-slate-300" size={16} />
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-slate-100">
                {stats.bandwidth.total_upload_gb} GB
              </p>
            </div>
          </div>
        </div>
      )}

      {/* System Info */}
      {stats.system && (
        <div className="mb-4">
          <h2 className="text-base font-semibold text-gray-900 mb-3 dark:text-slate-100">System Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 dark:bg-slate-800 dark:border-slate-700">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs text-gray-600 dark:text-slate-300">CPU Usage</p>
                <Cpu className="text-indigo-600 dark:text-indigo-400" size={16} />
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-slate-100">
                {stats.system.cpu_usage}
              </p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 dark:bg-slate-800 dark:border-slate-700">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs text-gray-600 dark:text-slate-300">Memory Usage</p>
                <Activity className="text-purple-600 dark:text-purple-400" size={16} />
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-slate-100">
                {stats.system.memory_usage}
              </p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 dark:bg-slate-800 dark:border-slate-700">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs text-gray-600 dark:text-slate-300">Uptime</p>
                <Clock className="text-green-600 dark:text-green-400" size={16} />
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-slate-100">
                {Math.floor(stats.system.uptime_seconds / 86400)}d
              </p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 dark:bg-slate-800 dark:border-slate-700">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs text-gray-600 dark:text-slate-300">Connected Clients</p>
                <Users className="text-teal-600 dark:text-teal-400" size={16} />
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-slate-100">
                {stats.network?.connected_clients || 0}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* WiFi Settings */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-4 dark:bg-slate-800 dark:border-slate-700">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-1.5 dark:text-slate-100">
            <Wifi size={16} className="text-indigo-600 dark:text-indigo-400" />
            WiFi Settings
          </h2>
          <button
            onClick={() => setShowWifiForm(!showWifiForm)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-md hover:bg-indigo-700 transition-colors"
          >
            <Settings size={14} />
            {showWifiForm ? 'Tutup' : 'Ubah WiFi'}
          </button>
        </div>

        {showWifiForm && (
          <div className="space-y-3 pt-3 border-t border-gray-200 dark:border-slate-700">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5 dark:text-slate-200">
                Nama WiFi (SSID)
              </label>
              <input
                type="text"
                value={wifiSsid}
                onChange={(e) => setWifiSsid(e.target.value)}
                placeholder="Masukkan nama WiFi baru"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-600"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5 dark:text-slate-200">
                Password WiFi
              </label>
              <input
                type="password"
                value={wifiPassword}
                onChange={(e) => setWifiPassword(e.target.value)}
                placeholder="Minimal 8 karakter"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-600"
              />
              <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">Minimal 8 karakter sesuai aturan ONT</p>
            </div>

            <button
              onClick={handleWifiUpdate}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 transition-colors font-medium"
            >
              <Wifi size={14} />
              Update WiFi Settings
            </button>
          </div>
        )}

        {!showWifiForm && device?.wireless && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-gray-200 dark:border-slate-700">
            <div>
              <p className="text-xs text-gray-500 mb-0.5 dark:text-slate-400">SSID 2.4GHz</p>
              <p className="text-sm font-medium text-gray-900 dark:text-slate-100">{device.wireless.ssid_2_4ghz || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5 dark:text-slate-400">SSID 5GHz</p>
              <p className="text-sm font-medium text-gray-900 dark:text-slate-100">{device.wireless.ssid_5ghz || '-'}</p>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 dark:bg-slate-800 dark:border-slate-700">
        <h2 className="text-base font-semibold text-gray-900 mb-3 dark:text-slate-100">Device Actions</h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleDeviceAction('reboot')}
            className="flex items-center gap-1.5 px-3 py-2 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700 transition-colors"
          >
            <Power size={14} />
            Reboot Device
          </button>
          
          <button
            onClick={() => handleDeviceAction('resync')}
            className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
          >
            <RefreshCw size={14} />
            Resync Device
          </button>

          <button
            onClick={() => handleDeviceAction('factory_reset')}
            className="flex items-center gap-1.5 px-3 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors"
          >
            <Settings size={14} />
            Factory Reset
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeviceDetail;
